import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';

interface HeaderContent {
  title?: ReactNode;
  onBack?: () => void;
}

interface HeaderContextValue extends HeaderContent {
  setHeader: (content: HeaderContent) => void;
}

const HeaderContext = createContext<HeaderContextValue | undefined>(undefined);

export const HeaderProvider = ({ children }: { children: ReactNode }) => {
  const [header, setHeaderState] = useState<HeaderContent>({});

  const setHeader = useCallback((content: HeaderContent) => {
    setHeaderState((prev) =>
      prev.title === content.title && prev.onBack === content.onBack ? prev : content,
    );
  }, []);

  return (
    <HeaderContext.Provider value={{ ...header, setHeader }}>{children}</HeaderContext.Provider>
  );
};

export const useHeaderContext = () => {
  const ctx = useContext(HeaderContext);
  if (!ctx) throw new Error('useHeaderContext must be used within a HeaderProvider');
  return ctx;
};

// Lets a page register its title/back-button in the shared AppHeader; clears on unmount.
export const usePageHeader = (content: HeaderContent) => {
  const { setHeader } = useHeaderContext();
  const { title, onBack } = content;

  const clear = useCallback(() => setHeader({}), [setHeader]);

  useEffect(() => {
    setHeader({ title, onBack });
    return clear;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, onBack]);
};
